import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, type Pagina } from '../comun';
import type { components as Entradas } from '../tipos/eventos-entradas';
import type { components as Listas } from '../tipos/listas-rrpp';

/**
 * Operaciones de «Mis entradas» (10.15, token de cliente) y de la página pública «Salir de la lista» (10.16, anónima,
 * G755, G767), con su `operationId` y los tipos generados de `eventos-entradas.yaml` y `listas-rrpp.yaml`. Van con el
 * cliente de la API de la zona del cliente (`cliente/apiCliente.ts`), nunca con la sesión del equipo.
 */
export type MiPedido = Entradas['schemas']['MyOrder'];
export type ListaMisPedidos = Entradas['schemas']['MyOrderList'];
export type EstadoPedido = Entradas['schemas']['OrderStatus'];
export type EnlaceSalida = Listas['schemas']['GuestLeaveLinkInfo'];
export type IdiomaSalida = 'es' | 'en';

const perfil = (id: string): `/v1/${string}` => `/v1/customer-account/profiles/${encodeURIComponent(id)}`;
const salida = (token: string): `/v1/${string}` => `/v1/guest-leave-links/${encodeURIComponent(token)}`;

export function operacionesMisEntradas(api: ClienteApi) {
    return {
        /** listMyTicketOrders: sus pedidos en un negocio, próximos (por defecto) o pasados; un perfil que no es suyo, 404. */
        listMyTicketOrders: (perfilId: string, cuando: 'upcoming' | 'past' = 'upcoming', pagina: Pagina = {}) =>
            api.pedir<ListaMisPedidos>('GET', `${perfil(perfilId)}/ticket-orders${consulta({ when: cuando, ...pagina })}`),
        /** getMyTicketOrder. */
        getMyTicketOrder: (perfilId: string, pedidoId: string) => api.pedir<MiPedido>('GET', `${perfil(perfilId)}/ticket-orders/${encodeURIComponent(pedidoId)}`),
        /** cancelMyTicketOrder: solo tras un cambio real de fecha y dentro del plazo (G17, G20); si no, `409`. */
        cancelMyTicketOrder: (perfilId: string, pedidoId: string, clave = claveIdempotencia()) =>
            api.pedir<MiPedido>('POST', `${perfil(perfilId)}/ticket-orders/${encodeURIComponent(pedidoId)}/cancellation`, { idempotencia: clave }),

        /** getGuestLeaveLink (anónima): negocio, evento y lista; **no escribe nada** al abrir (G155). */
        getGuestLeaveLink: (token: string, idioma: IdiomaSalida) =>
            api.pedir<EnlaceSalida>('GET', salida(token), { sinSesion: true, cabeceras: { 'Accept-Language': idioma } }),
        /** confirmGuestLeave (anónima): `204`; lista cerrada `409`; enlace inservible `404`; `429`. */
        confirmGuestLeave: (token: string, clave = claveIdempotencia()) => api.pedir<undefined>('POST', salida(token), { sinSesion: true, idempotencia: clave }),
    };
}

export type OperacionesMisEntradas = ReturnType<typeof operacionesMisEntradas>;
